import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useTrace } from '../context/TraceContext';
import { useApp } from '../context/AppContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { 
  Users, Shield, Settings, Key, Plus
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { roles } from '../data/mockData';

const AdminPage = () => {
  const { pidTraceMode } = useTrace();
  const { isReadOnly } = useApp();

  const sampleUsers = [
    { id: 'USR-001', name: 'Dr. Anita Kulkarni', email: 'anita.k@pharma.com', role: 'QA Head', status: 'Active', lastLogin: '2 hours ago' },
    { id: 'USR-002', name: 'Rajesh Kumar', email: 'rajesh.k@pharma.com', role: 'Operator', status: 'Active', lastLogin: '30 min ago' },
    { id: 'USR-003', name: 'Priya Sharma', email: 'priya.s@pharma.com', role: 'Production Supervisor', status: 'Active', lastLogin: '1 hour ago' },
    { id: 'USR-004', name: 'Amit Patel', email: 'amit.p@pharma.com', role: 'QC Analyst', status: 'Active', lastLogin: '45 min ago' },
    { id: 'USR-005', name: 'External Auditor', email: 'auditor@external.com', role: 'External Auditor', status: 'Active', lastLogin: '1 day ago' }
  ];

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Administration
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            User management, role configuration, and system settings
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* <ReportExportButton 
            title="Administration Report"
            subtitle="User management, role configuration, and system settings"
          /> */}
        </div>
      </div>

      <ReportSection id="admin-metrics" title="Administration Metrics" description="User and system statistics">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §9" req="SM-DI-001" />
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Total Users</p>
              <p className="text-2xl font-bold text-slate-900">{sampleUsers.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Active Roles</p>
              <p className="text-2xl font-bold text-blue-600">{roles.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Active Now</p>
              <p className="text-2xl font-bold text-emerald-600">12</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Audit Trail</p>
              <p className="text-2xl font-bold text-emerald-600">100%</p>
            </CardContent>
          </Card>
        </div>
      </ReportSection>

      <Tabs defaultValue="users">
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger value="users" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <Users className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="roles" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <Shield className="h-4 w-4 mr-2" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4 space-y-4">
          <div className="flex justify-end">
            {!isReadOnly && (
              <Button className="bg-[#fd4816] hover:bg-[#ea380c] text-white" data-testid="add-user">
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            )}
          </div>

          <ReportSection id="users-list" title="Users List" description="All system users">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §9" req="SM-TRN-001" />
              <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">ID</TableHead>
                  <TableHead className="text-slate-600">Name</TableHead>
                  <TableHead className="text-slate-600">Email</TableHead>
                  <TableHead className="text-slate-600">Role</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">Last Login</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sampleUsers.map(user => (
                  <TableRow key={user.id} className="hover:bg-slate-50 cursor-pointer">
                    <TableCell className="font-mono text-xs text-slate-500">{user.id}</TableCell>
                    <TableCell className="text-sm text-slate-800 font-medium">{user.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{user.email}</TableCell>
                    <TableCell className="text-sm text-slate-600">{user.role}</TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">{user.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{user.lastLogin}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <ReportSection id="roles-list" title="Roles Configuration" description="System roles and permissions">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §7.2" req="SM-DI-001" />
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">Role Configuration</CardTitle>
              </CardHeader>
              <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">Role ID</TableHead>
                  <TableHead className="text-slate-600">Role Name</TableHead>
                  <TableHead className="text-slate-600">Permissions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map(role => (
                  <TableRow key={role.id} className="hover:bg-slate-50">
                    <TableCell className="font-mono text-xs text-slate-500">{role.id}</TableCell>
                    <TableCell className="text-sm text-slate-800 font-medium">{role.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {role.permissions.slice(0, 3).map((perm, idx) => (
                          <Badge key={idx} className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                            {perm}
                          </Badge>
                        ))}
                        {role.permissions.length > 3 && (
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                            +{role.permissions.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <ReportSection id="system-settings" title="System Settings" description="System configuration">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <CardContent className="p-8 text-center">
                <Settings className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <h3 className="text-lg font-medium text-slate-800 mb-2">System Settings</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Configure system-wide settings, audit policies, and compliance parameters.
                </p>
                <p className="text-xs text-slate-500">Feature available in full implementation</p>
              </CardContent>
            </Card>
          </ReportSection>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
